import { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import KpiReportLayout from '@/components/reports/KpiReportLayout'
import {
  buildSdSummaryRows,
  formatFilterRange,
  formatThresholds,
  SD_ACK_DETAIL_COLUMNS,
  SD_CLOSING_DETAIL_COLUMNS,
  SD_CREATION_DETAIL_COLUMNS,
} from '@/components/reports/kpiReportColumns'
import { helpdeskReportService, type ReportFilters, type SdKpiResponse } from '@/lib/services/helpdeskReportService'

const emptyData: SdKpiResponse = {
  summary: {},
  metric_breakdown: [],
  details: {},
  filters: {},
}

export default function SdKpiReportPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [data, setData] = useState<SdKpiResponse>(emptyData)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getSdKpi(filters)
      .then(setData)
      .catch(() => setData(emptyData))
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id])

  const periodLabel = formatFilterRange(data.filters ?? filters)

  const detailTabs = useMemo(
    () => [
      {
        label: 'Acknowledgements',
        columns: SD_ACK_DETAIL_COLUMNS,
        rows: data.details?.acknowledgements?.rows ?? [],
        total: data.details?.acknowledgements?.total ?? 0,
        shown: data.details?.acknowledgements?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.request_id ?? index),
      },
      {
        label: 'Closings',
        columns: SD_CLOSING_DETAIL_COLUMNS,
        rows: data.details?.closings?.rows ?? [],
        total: data.details?.closings?.total ?? 0,
        shown: data.details?.closings?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.ticket_id ?? index),
      },
      {
        label: 'Creations',
        columns: SD_CREATION_DETAIL_COLUMNS,
        rows: data.details?.creations?.rows ?? [],
        total: data.details?.creations?.total ?? 0,
        shown: data.details?.creations?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) =>
          String(row.ticket_id ?? index),
      },
    ],
    [data.details]
  )

  return (
    <Box p={4}>
      <ReportPageHeader
        title="SD KPI"
        subtitle="Service desk acknowledgement, closing, and creation metrics"
        reportType="sd-kpi"
        filters={filters}
        onFiltersChange={setFilters}
      />
      <KpiReportLayout
        loading={loading}
        periodLabel={periodLabel}
        thresholdNote={formatThresholds(data.thresholds)}
        summaryRows={buildSdSummaryRows(data.summary)}
        breakdownRows={data.metric_breakdown ?? []}
        detailTabs={detailTabs}
      />
    </Box>
  )
}
